import {
  Button,
  IconButton,
  Spacer,
  SvgRecord,
  SvgVideoCam,
  TextButton,
} from '@loomhq/lens';
import { isSupported } from '@loomhq/loom-sdk';
import cn from 'classnames';
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uaParser from 'ua-parser-js';

import { SDK_BROWSER_COMPATIBILITY_TEST_FAILED } from 'constants/events';
import {
  EXP_RECORD_REPLY_LOGGED_IN_USERS,
  EXP_RECORD_REPLY_LOGGED_OUT_USERS,
} from 'constants/featureFlag';
import { SHOW_RECORD_A_REPLY_TOOLTIP } from 'constants/triggers';
import { EDUCATION as WORKSPACE_PERSONA_EDUCATION } from 'constants/useCasePlans';
import { WORKSPACE_PLAN_EDUCATION } from 'constants/workspacePlans';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';

import useMedia from 'hooks/useMedia';
import { useShouldShowTrigger } from 'hooks/triggers';
import {
  selectShareVideoWorkspacePersona,
  selectShareVideoWorkspacePlan,
  selectVideoId,
} from 'selectors/shareVideo';
import { selectUserId, selectUserIsLogged } from 'selectors/user';
import { selectOwnerName, selectRecordReplyEnabled } from 'selectors/video';
import { selectCurrentUserIsOwner } from 'selectors/videoPlayer';

import RecordReplyTooltip from '../../share-video/engagement-bar/record-reply-tooltip';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import styles from './styles.module.less';

const TextIconButton = props => {
  const {
    compact,
    tooltipActive,
    textButtonSize = 'medium',
    buttonText,
  } = props;

  return compact ? (
    <IconButton isDisabled icon={<SvgVideoCam />} />
  ) : (
    <div className={cn('px:xsmall', { [styles.tooltipActive]: tooltipActive })}>
      <TextButton isDisabled icon={<SvgVideoCam />} size={textButtonSize}>
        {buttonText}
      </TextButton>
    </div>
  );
};

export const RecordReplyWrapper = props => {
  const isLoggedIn = useSelector(selectUserIsLogged);
  const recordReplyEnabled = useSelector(selectRecordReplyEnabled);
  const isCurrentUserOwner = useSelector(selectCurrentUserIsOwner);
  const ownerName = useSelector(selectOwnerName);
  const videoWorkspacePersona = useSelector(selectShareVideoWorkspacePersona);
  const videoWorkspacePlan = useSelector(selectShareVideoWorkspacePlan);
  const userId = useSelector(selectUserId);
  const videoId = useSelector(selectVideoId);
  const dispatch = useDispatch();
  const [isSdkSupported, setIsSdkSupported] = useState(false);
  const [sdkNotSupportedReason, setSdkNotSupportedReason] = useState();

  const dismissRecordReplyTooltip = () => {
    dispatch(completeTriggerCreator(SHOW_RECORD_A_REPLY_TOOLTIP));
  };

  const shouldShowRecordReplyTooltip =
    useShouldShowTrigger(SHOW_RECORD_A_REPLY_TOOLTIP) &&
    props.iconButton &&
    !isCurrentUserOwner;
  const fullSize = useMedia(['(min-width: 560px)'], [true], false);
  let textButtonSize = fullSize ? 'medium' : 'small';

  if (props.forceSmallIcon) {
    textButtonSize = 'small';
  }

  useEffect(() => {
    (async () => {
      const { supported, error } = await isSupported();

      setIsSdkSupported(supported);
      setSdkNotSupportedReason(error);
    })();
  }, []);

  const isEDUVideo =
    videoWorkspacePersona?.use_case_plan === WORKSPACE_PERSONA_EDUCATION ||
    videoWorkspacePlan === WORKSPACE_PLAN_EDUCATION;

  const experimentAllowed =
    recordReplyEnabled && isSdkSupported && !isEDUVideo && !isMobile;

  useEffect(() => {
    // If all of our precondition checks pass except for isSdkSupported we want to
    // log an event to help exclude these users from our data and to get insight into
    // what browsers/os are not passing the test if it's due to a browser compatibility issue
    if (!isSdkSupported && recordReplyEnabled && !isEDUVideo && !isMobile) {
      const parser = new uaParser();
      const uaDetails = parser.getResult();

      analytics.track(SDK_BROWSER_COMPATIBILITY_TEST_FAILED, {
        userId,
        videoId,
        browserName: uaDetails?.browser?.name,
        browserVersion: uaDetails?.browser?.version,
        osName: uaDetails?.os?.name,
        osVersion: uaDetails?.os?.version,
        sdkNotSupportedReason,
        userAgent: uaDetails?.ua,
      });
    }
  }, [
    isSdkSupported,
    isLoggedIn,
    sdkNotSupportedReason,
    recordReplyEnabled,
    isEDUVideo,
    userId,
    videoId,
  ]);

  if (experimentAllowed) {
    return (
      <FeatureFlaggedComponent
        flag={
          isLoggedIn
            ? EXP_RECORD_REPLY_LOGGED_IN_USERS
            : EXP_RECORD_REPLY_LOGGED_OUT_USERS
        }
        activationValues={['variant']}
      >
        <Spacer left="small" />

        <RecordReplyFallbackButton
          {...props}
          shouldShowRecordReplyTooltip={shouldShowRecordReplyTooltip}
          dismissRecordReplyTooltip={dismissRecordReplyTooltip}
          textButtonSize={textButtonSize}
        />

        {shouldShowRecordReplyTooltip && (
          <RecordReplyTooltip
            ownerName={ownerName}
            onClick={dismissRecordReplyTooltip}
          />
        )}
      </FeatureFlaggedComponent>
    );
  }

  return null;
};

export const RecordReplyWrapperMemo = React.memo(RecordReplyWrapper);

const RecordReplyFallbackButton = props => {
  const LazyRecordAReply = React.lazy(() => import('./record-reply'));

  const {
    iconButton,
    compact,
    shouldShowRecordReplyTooltip,
    setHasRecordReply = () => {},
    textButtonSize,
    buttonText = 'Record a Reply',
  } = props;

  useEffect(() => {
    setHasRecordReply(true);
  }, [setHasRecordReply]);

  return (
    <Suspense
      fallback={
        // Render a fake button as a fallback
        iconButton ? (
          <TextIconButton
            compact={compact}
            tooltipActive={shouldShowRecordReplyTooltip}
            textButtonSize={textButtonSize}
            buttonText={buttonText}
          />
        ) : (
          <div className="bgc:white radius:medium theme-light">
            <Button
              variant="neutral"
              icon={<SvgRecord color="#ff623e" />}
              isDisabled
            >
              {buttonText}
            </Button>
          </div>
        )
      }
    >
      <LazyRecordAReply iconButton={iconButton} compact={compact} {...props} />
    </Suspense>
  );
};

export default RecordReplyFallbackButton;
